import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useMarket, useMarketMetadata } from '@//providers/MarketSettingsProvider';
import type { EnrichedElasticSearchResponseFacet } from '@//utils/vehicles-search/useEnrichedFacets';
import useActiveFacets from './useActiveFacets';
import { getLocationActiveFacetLabel, getRangeActiveFacetLabel } from './utils';

interface UseActiveFiltersSummaryLabel {
  enrichedFacets?: EnrichedElasticSearchResponseFacet[];
  params: URLSearchParams;
}

const useActiveFiltersSummaryLabel = ({ enrichedFacets, params }: UseActiveFiltersSummaryLabel) => {
  const market = useMarket();
  const marketMetadata = useMarketMetadata();
  const locale = useLocale();
  const t = useTranslations('utils.search');

  const paramsString = params.toString();

  const activeFilters = useActiveFacets({
    enrichedFacets,
    searchParams: params,
  });

  const summaryLabel = useMemo(() => {
    const labels: string[] = [];

    const q = params.get('q');
    if (q) labels.push(`"${q}"`);

    if (activeFilters) {
      for (const f of activeFilters) {
        if (f.id === 'location') {
          labels.push(getLocationActiveFacetLabel(params));
        } else if (f.type === 'range') {
          labels.push(
            getRangeActiveFacetLabel({
              facet: f,
              searchParams: params,
              locale,
              market,
              currencySymbol: marketMetadata.currencySymbol,
              t,
            }),
          );
        } else if (f.type === 'toggle') {
          labels.push(f.displayName);
        } else {
          const selectedFilters = f.filters
            .filter((flt) => params.getAll(f.id).includes(flt.displayName))
            .map((flt) => flt.displayName);
          if (selectedFilters.length) {
            labels.push(selectedFilters.join(', '));
          }
        }
      }
    }

    return labels.join(', ');
  }, [activeFilters, paramsString, marketMetadata.currency, locale]);

  return summaryLabel;
};

export default useActiveFiltersSummaryLabel;
